/* eslint-disable no-unused-vars,semi-style */
import { selectors } from 'app-webtech-core';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import parseISO from 'date-fns/parseISO';
import { HEARTBEAT } from '../modules/playbackSession/actions/types';
import { PLAYER_CONTENT_KIND } from '../modules/player/constants';
import {
  CLIP_ENDS,
  CLIP_STARTS,
  EPISODE_ENDS,
  EPISODE_STARTS,
} from '../modules/player/actions/types';
import { applicationPropertiesSelector } from '../modules/application/selectors/properties';
import { guidePropertiesSelector } from '../modules/guide/selectors/properties';
import { playerPropertiesSelector } from '../modules/player/selectors/properties';
import { playbackSessionPropertiesSelector } from '../modules/playbackSession/selectors/properties';
import { getChannelByTimelineId } from '../modules/playbackSession/containers/helpers/getChannelByTimelineId';
import { getChannelIdByIndex } from '../modules/guide/helpers/getChannelIdByIndex';
import { type2Beacon } from './index';

export const getCurrentClipData = (state) => {
  const { clips } = selectors.playbackSession.properties(state);
  if (!clips || clips.length === 0 || !clips[0]) {
    return undefined;
  }
  return clips[0];
};

const getClipTimelinePoint = (state) => {
  const currentClip = getCurrentClipData(state);
  if (!currentClip) {
    return -1;
  }
  const clipStartTime = parseISO(currentClip.startTime);
  return differenceInSeconds(Date.now(), clipStartTime);
};

const getEpisodeTimelinePoint = (state) => {
  const currentClip = getCurrentClipData(state);
  if (!currentClip) {
    return -1;
  }
  const episodeStartTime = parseISO(currentClip.timelineStartAt);
  return differenceInSeconds(Date.now(), episodeStartTime);
};

const getVideoBitRate = (state) => {
  const {
    player: { bitrate },
  } = state;
  return bitrate;
};

const getFeaturedChannelIndex = (state) => {
  let resIdx = -1;
  const { currentPlayingIndex, list, featuredCategory } = selectors.guide.properties(state);
  if (
    featuredCategory &&
    currentPlayingIndex &&
    currentPlayingIndex >= featuredCategory.starstAtIndex &&
    currentPlayingIndex <= featuredCategory.endsAtIndex
  ) {
    const featured = list.slice(featuredCategory.starstAtIndex, featuredCategory.endsAtIndex + 1);
    const channelId = list[currentPlayingIndex] ? list[currentPlayingIndex].id : undefined;
    resIdx = featured.findIndex((item) => item.id === channelId); // -1 iff !found
  }
  return resIdx;
};

const getPlayerHeight = (state) => -1; // TODO
const getPlayerWidth = (state) => -1; // TODO
const getScreenState = (state) => {
  const player = playerPropertiesSelector(state);
  if (!player) {
    return 'na';
  }
  return player.isFullscreen ? 'fullscreen' : 'guide';
};

const checkActionType = (checkType) =>
  [CLIP_ENDS, CLIP_STARTS, EPISODE_ENDS, EPISODE_STARTS, HEARTBEAT].find(
    (type) => type2Beacon(type) === checkType,
  );

const getIsAutoPlay = (action, state) => {
  if (checkActionType(action.type) || action.type.includes('POD_TRACKER_ADS_CM')) {
    const { isAutoPlay } = guidePropertiesSelector(state);
    return isAutoPlay;
  }
  return false;
};

const getPreviousChannelID = (state) => {
  const { videoRequested, content } = selectors.player.properties(state);
  const {
    previousContentKind,
    currentPlayingIndex,
    previousPlayingIndex,
  } = selectors.guide.properties(state);

  const { trackChannelEvents } = selectors.playbackSession.properties(state);
  if (trackChannelEvents && previousContentKind && videoRequested) {
    if (previousContentKind !== PLAYER_CONTENT_KIND.LINEAR_CHANNEL) {
      return previousContentKind.toLowerCase();
    }
    if (content.kind === PLAYER_CONTENT_KIND.VOD) {
      return getChannelIdByIndex(state, currentPlayingIndex);
    }
    return getChannelIdByIndex(state, previousPlayingIndex);
  }
  return 'na';
};

const getCurrentChannelID = (state) => {
  const { videoRequested, content } = selectors.player.properties(state);
  const { trackChannelEvents } = selectors.playbackSession.properties(state);
  const clip = getCurrentClipData(state);
  if (trackChannelEvents) {
    if (clip && !videoRequested) {
      if (content && content.kind === PLAYER_CONTENT_KIND.VOD) {
        return 'vod';
      }
      return getChannelByTimelineId(clip.timelineID, state);
    }
    if ((content && !clip && !videoRequested) || videoRequested) {
      if (content.kind && content.kind !== PLAYER_CONTENT_KIND.LINEAR_CHANNEL) {
        return content.kind.toLowerCase();
      }
      return content.id;
    }
  }
  return 'na';
};

const getCurrentPlaybackData = (state) => {
  const { videoRequested, content } = selectors.player.properties(state);
  const { previousContentKind, currentPlayingIndex } = selectors.guide.properties(state);
  const { trackClipEvents } = selectors.playbackSession.properties(state);
  const clip = getCurrentClipData(state);
  if (clip && trackClipEvents) {
    if (videoRequested) {
      if (
        (content.kind === PLAYER_CONTENT_KIND.VOD && content.id === clip.episodeID) ||
        (getChannelByTimelineId(clip.timelineID, state) ===
          getChannelIdByIndex(state, currentPlayingIndex) &&
          (content.kind !== PLAYER_CONTENT_KIND.VOD ||
            previousContentKind !== PLAYER_CONTENT_KIND.LINEAR_CHANNEL))
      ) {
        return clip;
      }
    } else {
      return clip;
    }
  }
  if (content && content.kind === 'viral') {
    return { clipID: content.id };
  }
  return {};
};

/* const getSection = (state) => {
  const { launchState } = selectors.application.properties(state);
  if (launchState === LAUNCH_STATE.APP_LOADED) {
    const { section } = selectors.layout.properties(state);
    if (!section) {
      return selectors.location.page(state) === 'Watch' ? 'live' : 'vod';
    }
    return section;
  }
  return 'na';
}; */

const getPreviousSection = (action, state) => {
  const { previousSection } = selectors.layout.properties(state);
  if (previousSection) {
    return previousSection;
  }
  return 'na';
};

/**
 * Example usage:
 *   [ACTION_TYPE]: withFields((action, state) => {})
 */
export const withFields = (beacon) => (action, prevState, nextState) => {
  const event = beacon(action, prevState, nextState);
  if (!event) {
    return null;
  }

  const nextPlaybackSession = playbackSessionPropertiesSelector(nextState);
  const applicationFields = applicationPropertiesSelector(nextState);
  if (!applicationFields.isConfigured) {
    console.warn('FE fields not configured. Did you forgot to call APPLICATION_SETUP action?');
    applicationFields.setup = {};
  }

  const { clipID = 'na', episodeID = 'na', timelineID = 'na' } = getCurrentPlaybackData(nextState);

  const userAccountData = selectors.userAccount.properties(nextState);
  const { section } = selectors.layout.properties(nextState);

  const {
    myIp,
    utmCampaign = 'na',
    utmContent = 'na',
    utmMedium = 'na',
    utmSource = 'na',
    utmTerm = 'na',
  } = applicationFields.setup;

  const featuredChannelIndex = getFeaturedChannelIndex(prevState);
  return {
    accountID: (userAccountData && userAccountData.data && userAccountData.data._id) || 'na',
    appName: applicationFields.appName,
    appVersion: applicationFields.appVersion,
    channelID: getCurrentChannelID(nextState) || 'na',
    clientIP: myIp,
    clientPrivateIP: 'na',
    clipID: clipID || 'na',
    clipTimelinePoint: getClipTimelinePoint(prevState),
    custom1: 0,
    custom2: 'na',
    custom3: 'na',
    episodeID: episodeID || 'na',
    episodeTimelinePoint: getEpisodeTimelinePoint(prevState),
    eventEmitterIP: myIp || 'na',
    eventGeneratorIP: myIp || 'na',
    featuredChannelIndex: featuredChannelIndex > 0 ? featuredChannelIndex : 0,
    isAutoPlay: getIsAutoPlay(action, nextState),
    isGuideUp: false,
    label: '',
    linkID: 'na',
    pageName: 'na',
    pairingCode: 'na',
    previousPageName: 'na',
    playerHeight: getPlayerHeight(prevState),
    playerWidth: getPlayerWidth(prevState),
    previousChannelID: getPreviousChannelID(nextState) || 'na',
    previousSection: getPreviousSection(action, nextState),
    programTimelineID: timelineID || 'na',
    screenState: getScreenState(prevState),
    sessionID: nextPlaybackSession ? nextPlaybackSession.ID : 'na',
    section: section || 'na',
    streamBitrate: getVideoBitRate(nextState),
    streamResolution: 'na',
    testGroupID: 'na',
    testID: 'na',
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    utmTerm,
    value: 0,
    volumeLevel: -1,
    ...applicationFields.setup,
    ...event,
  };
};
